import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { handleErrorMessage } from "helpers/methods";
import { SignRequest } from "models/interfaces";
import EmptyState from "global/components/EmptyState/EmptyState";
import { LibraryDocument, LibraryTemplate } from "./templateLibrary.types";
import {
  fetchLibraryTemplate,
  fetchLibraryTemplateDocuments,
  fetchLibraryTemplatePreview,
  importedTemplateIdFor,
  importLibraryTemplate,
  markImported,
} from "./TemplateLibraryService";
import {
  documentsLabel,
  formatUpdated,
  pagesLabel,
} from "./templateLibraryHelpers";
import LibraryDocumentPreview from "./components/LibraryDocumentPreview";
import templateLibraryStyles from "./TemplateLibrary.styles";
import {
  ArrowRightGlyph,
  CheckGlyph,
  ImportGlyph,
  KitGlyph,
  PdfFileGlyph,
  PenGlyph,
} from "./TemplateLibraryIcons";
import {
  ArrowLeftGlyph,
  ChevronRightGlyph,
} from "../components/TemplatesIcons";

const styles = templateLibraryStyles;

export interface LibraryActionPermissions {
  /** May start a sign request (sign · send). */
  canUse: boolean;
  /** May add templates (template · add, feature on). */
  canImport: boolean;
  /** Why import is blocked even though permitted, e.g. the plan's limit. */
  importBlockedReason?: string;
}

const KIT_STEPS = [
  {
    title: "Pick documents",
    text: "Keep or untick the documents you need.",
  },
  { title: "Import the kit", text: "It lands in My templates as one kit." },
  { title: "Customize", text: "Edit each document's fields and clauses." },
  {
    title: "Send as one package",
    text: "Recipient signs all documents in order.",
  },
  { title: "Track", text: "See progress per document." },
];

const SINGLE_STEPS = [
  { title: "Import", text: "Save a copy to My templates." },
  { title: "Customize", text: "Fill in parties, dates and clauses." },
  { title: "Add recipients", text: "Set signers and place fields." },
  { title: "Send", text: "Request signatures." },
  { title: "Track", text: "Follow progress to completion." },
];

interface TemplateLibraryDetailProps {
  templateId: string;
  onClose: () => void;
  onGoToMyTemplates: () => void;
  onUseTemplate: (template: SignRequest) => void;
  onImported: () => void;
  permissions: LibraryActionPermissions;
}

/**
 * One library template: every document and every page on the left (one tab
 * per document for a kit), details and actions on the right — including the
 * "Included in this kit" list for a template with several documents. If the
 * documents cannot be loaded, the cover image is shown instead. "Use" imports
 * a copy and starts the existing Create Request flow with it; "Import" only
 * saves the copy.
 */
const TemplateLibraryDetail = ({
  templateId,
  onClose,
  onGoToMyTemplates,
  onUseTemplate,
  onImported,
  permissions,
}: TemplateLibraryDetailProps) => {
  const [template, setTemplate] = useState<LibraryTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<LibraryDocument[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  // "documents": every PDF is shown. "cover"/"none": the documents could not
  // be loaded, so fall back to the cover image, or to nothing.
  const [previewState, setPreviewState] = useState<
    "loading" | "documents" | "cover" | "none"
  >("loading");
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  // Kit documents the user unticked (by index). Only the ticked ones are
  // imported — by "Use kit" and by "Import kit" alike. One always stays ticked.
  const [excluded, setExcluded] = useState<number[]>([]);
  const [busy, setBusy] = useState<"use" | "import" | null>(null);
  const [isImported, setIsImported] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setTemplate(null);
    setDocuments([]);
    setActiveIndex(0);
    setExcluded([]);
    setCoverUrl(null);
    setPreviewState("loading");
    setIsImported(!!importedTemplateIdFor(templateId));
    fetchLibraryTemplate(templateId)
      .then((result) => !cancelled && setTemplate(result))
      .catch((error) => !cancelled && handleErrorMessage(error))
      .finally(() => !cancelled && setIsLoading(false));

    const showCover = () =>
      fetchLibraryTemplatePreview(templateId)
        .then((url) => {
          if (cancelled) return;
          setCoverUrl(url || null);
          setPreviewState(url ? "cover" : "none");
        })
        .catch(() => !cancelled && setPreviewState("none"));

    fetchLibraryTemplateDocuments(templateId)
      .then((list) => {
        if (cancelled) return;
        const items = Array.isArray(list) ? list : [];
        if (items.length === 0) return showCover();
        setDocuments(items);
        setPreviewState("documents");
      })
      .catch(() => !cancelled && showCover());
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const isIncluded = (index: number) => !excluded.includes(index);

  /** Untick or re-tick a kit document; the last ticked one cannot be removed. */
  const toggleDocument = (index: number) =>
    setExcluded((off) => {
      if (off.includes(index)) return off.filter((i) => i !== index);
      return documents.length - off.length <= 1 ? off : [...off, index];
    });

  const importCopy = async () => {
    // A kit sends exactly the ticked documents; a single template sends none
    // (the backend then takes everything, as before).
    const selected =
      documents.length > 1
        ? documents.filter((_, i) => isIncluded(i)).map((d) => d.name)
        : [];
    const created = await importLibraryTemplate(templateId, selected);
    markImported(templateId, created.id);
    setIsImported(true);
    onImported();
    return created;
  };

  const handleUse = async () => {
    setBusy("use");
    try {
      onUseTemplate(await importCopy());
    } catch (error: any) {
      handleErrorMessage(error);
      setBusy(null);
    }
  };

  const handleImport = async () => {
    setBusy("import");
    try {
      await importCopy();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setBusy(null);
    }
  };

  const crumbs = (
    <Box component="nav" aria-label="Breadcrumb" sx={styles.crumbs}>
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={styles.crumbLink}
      >
        <ArrowLeftGlyph sx={styles.crumbBack} />
        Templates
      </Box>
      <ChevronRightGlyph sx={styles.crumbSeparator} />
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={styles.crumbLink}
      >
        Template library
      </Box>
      <ChevronRightGlyph sx={styles.crumbSeparator} />
      <Box component="span" aria-current="page" sx={styles.crumbCurrent}>
        {template?.name ?? ""}
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <Box aria-busy="true">
        {crumbs}
        <Box sx={styles.detailGrid}>
          <Skeleton
            variant="rounded"
            height={560}
            sx={{ borderRadius: "16px" }}
          />
          <Skeleton
            variant="rounded"
            height={420}
            sx={{ borderRadius: "16px" }}
          />
        </Box>
      </Box>
    );
  }

  if (!template) {
    return (
      <Box>
        {crumbs}
        <EmptyState
          framed
          icon="template"
          title="Template not found"
          body="This library template is no longer available."
          primaryAction={{
            label: "Back to Template library",
            onClick: onClose,
          }}
        />
      </Box>
    );
  }

  const importBlocked =
    !permissions.canImport || !!permissions.importBlockedReason;
  const showDocuments = previewState === "documents";
  const isKit = documents.length > 1 || (template.documentCount ?? 0) > 1;
  const activeDoc = documents[activeIndex] ?? documents[0];
  // Page counts are the server's, read from the PDFs on promote/refresh. 0
  // means "not counted yet", so it is hidden rather than shown as a number.
  const activePages = activeDoc?.pageCount ?? 0;
  const includedIndexes = documents
    .map((_, i) => i)
    .filter((i) => isIncluded(i));
  const selectedCount = includedIndexes.length;
  // The selected documents' total; a partial sum would understate it, so it
  // needs every selected document counted. Without the document list, the
  // card's whole-template total is all there is.
  const allCounted =
    selectedCount > 0 &&
    includedIndexes.every((i) => documents[i].pageCount > 0);
  const totalPages =
    documents.length === 0
      ? template.pageCount ?? 0
      : allCounted
        ? includedIndexes.reduce((sum, i) => sum + documents[i].pageCount, 0)
        : 0;
  const updated = formatUpdated(template.updatedOn);
  const stats = [
    isKit && documents.length > 0
      ? {
          label: "DOCUMENTS",
          value: `${selectedCount} of ${documents.length}`,
        }
      : null,
    totalPages > 0 ? { label: "PAGES", value: String(totalPages) } : null,
    updated ? { label: "UPDATED", value: updated } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  /** "3 pages · Employer + Employee" — whatever is known for the document. */
  const documentMeta = (doc: LibraryDocument) =>
    [
      doc.pageCount > 0 ? pagesLabel(doc.pageCount) : "",
      (doc.signerRoles ?? []).join(" + "),
    ]
      .filter(Boolean)
      .join(" · ");

  return (
    <Box>
      {crumbs}
      <Box sx={styles.detailGrid}>
        {/* Preview: every document, every page */}
        <Box sx={styles.docPane}>
          {showDocuments && documents.length > 1 && (
            <Box
              sx={styles.kitTabs}
              role="tablist"
              aria-label="Documents in this kit"
            >
              {documents.map((doc, i) => {
                const active = i === activeIndex;
                return (
                  <Box
                    key={`${doc.name}-${i}`}
                    component="button"
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveIndex(i)}
                    sx={styles.kitTab(active, isIncluded(i))}
                  >
                    <Box component="span" sx={styles.kitTabNum(active)}>
                      {i + 1}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box component="span" sx={styles.kitTabName(active)}>
                        {doc.name}
                      </Box>
                      {doc.pageCount > 0 && (
                        <Box component="span" sx={styles.kitTabPages}>
                          {pagesLabel(doc.pageCount)}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
          <Box sx={styles.fileBar}>
            <PdfFileGlyph
              sx={{ width: 15, height: 15, color: styles.pdfIconColor }}
            />
            <Box component="span" sx={styles.fileName}>
              {showDocuments && activeDoc ? activeDoc.name : template.name}
            </Box>
            {showDocuments && activePages > 0 && (
              <Box component="span" sx={styles.filePages}>
                {pagesLabel(activePages)}
              </Box>
            )}
            <Box
              component="span"
              sx={[
                styles.zoomChip,
                showDocuments && activePages > 0 ? {} : { ml: "auto" },
              ]}
            >
              100%
            </Box>
          </Box>
          <Box sx={styles.pages}>
            {showDocuments && activeDoc ? (
              <LibraryDocumentPreview
                key={`${activeDoc.name}-${activeIndex}`}
                document={activeDoc}
              />
            ) : previewState === "cover" && coverUrl ? (
              <Box sx={styles.page}>
                <Box
                  component="img"
                  src={coverUrl}
                  alt={`Preview of ${template.name}`}
                  onError={() => setPreviewState("none")}
                  sx={styles.previewImage}
                />
              </Box>
            ) : (
              <Box sx={styles.pageLoading}>
                {previewState === "loading" ? (
                  <CircularProgress size={26} aria-label="Loading preview" />
                ) : (
                  <Typography sx={styles.previewUnavailable}>
                    Preview not available.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Details + actions */}
        <Box sx={styles.side}>
          <Box sx={styles.panel}>
            <Box sx={styles.panelTags}>
              {template.category && (
                <Box component="span" sx={styles.categoryPill}>
                  {template.category.toUpperCase()}
                </Box>
              )}
              {template.featured && (
                <Box component="span" sx={styles.popularPill}>
                  POPULAR
                </Box>
              )}
            </Box>
            <Typography component="h1" sx={styles.title}>
              {template.name}
            </Typography>
            <Typography component="p" sx={styles.desc}>
              {template.longDescription || template.shortDescription}
            </Typography>

            {stats.length > 0 && (
              <Box sx={styles.stats(stats.length)}>
                {stats.map((stat) => (
                  <Box key={stat.label} sx={styles.stat}>
                    <Box component="span" sx={styles.statLabel}>
                      {stat.label}
                    </Box>
                    <Box component="span" sx={styles.statValue}>
                      {stat.value}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {isKit && documents.length > 0 && (
              <Box sx={styles.kitSection}>
                <Box sx={styles.kitListHead}>
                  <Box component="span" sx={styles.statLabel}>
                    INCLUDED IN THIS KIT
                  </Box>
                  <Box component="span" role="status" sx={styles.kitCount}>
                    {selectedCount} selected
                  </Box>
                </Box>
                <Box sx={styles.kitList}>
                  {documents.map((doc, i) => {
                    const active = showDocuments && i === activeIndex;
                    const included = isIncluded(i);
                    // The last ticked document cannot be unticked, and after
                    // the import the choice is final.
                    const locked =
                      isImported || (included && selectedCount === 1);
                    const meta = documentMeta(doc);
                    return (
                      <Box key={`${doc.name}-${i}`} sx={styles.kitRow}>
                        <Tooltip
                          title={
                            !isImported && included && selectedCount === 1
                              ? "A kit needs at least one document"
                              : ""
                          }
                          arrow
                          placement="top"
                        >
                          <Box
                            component="button"
                            type="button"
                            role="checkbox"
                            aria-checked={included}
                            aria-disabled={locked}
                            aria-label={`Include ${doc.name}`}
                            onClick={() => !locked && toggleDocument(i)}
                            sx={styles.checkbox(included, locked)}
                          >
                            {included && (
                              <CheckGlyph sx={{ width: 11, height: 11 }} />
                            )}
                          </Box>
                        </Tooltip>
                        <Box
                          component="button"
                          type="button"
                          aria-label={`Preview ${doc.name}`}
                          aria-current={active ? "true" : undefined}
                          onClick={() => setActiveIndex(i)}
                          sx={styles.kitRowBody}
                        >
                          <Box
                            component="span"
                            sx={styles.kitRowName(included)}
                          >
                            {doc.name}
                          </Box>
                          {meta && (
                            <Box component="span" sx={styles.kitRowMeta}>
                              {meta}
                            </Box>
                          )}
                        </Box>
                        <Box component="span" sx={styles.kitRowNum}>
                          #{i + 1}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={styles.kitNote}>
                  <KitGlyph
                    sx={{
                      width: 14,
                      height: 14,
                      flexShrink: 0,
                      mt: "1px",
                      color: styles.kitNoteIconColor,
                    }}
                  />
                  Sent as one package. The recipient signs every document in
                  this order from a single email.
                </Box>
              </Box>
            )}

            {isImported ? (
              <Box sx={styles.actions}>
                <Box sx={styles.importedBanner} role="status">
                  <CheckGlyph
                    sx={{
                      width: 16,
                      height: 16,
                      color: styles.importedIconColor,
                    }}
                  />
                  {isKit
                    ? "Kit imported to My templates"
                    : "Imported to My templates"}
                </Box>
                <Button
                  disableRipple
                  onClick={onGoToMyTemplates}
                  sx={styles.primaryButton}
                >
                  Go to My templates
                  <ArrowRightGlyph sx={{ width: 15, height: 15 }} />
                </Button>
              </Box>
            ) : (
              <Box sx={styles.actions}>
                {permissions.canUse && permissions.canImport && (
                  <Tooltip
                    title={permissions.importBlockedReason ?? ""}
                    arrow
                    describeChild
                  >
                    <Box component="span" sx={{ display: "block" }}>
                      <Button
                        disableRipple
                        disabled={busy !== null || importBlocked}
                        onClick={handleUse}
                        sx={styles.primaryButton}
                      >
                        {busy === "use" ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "inherit" }}
                          />
                        ) : (
                          <PenGlyph sx={{ width: 16, height: 16 }} />
                        )}
                        {isKit ? "Use kit" : "Use template"}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
                {permissions.canImport && (
                  <Tooltip
                    title={permissions.importBlockedReason ?? ""}
                    arrow
                    describeChild
                  >
                    <Box component="span" sx={{ display: "block" }}>
                      <Button
                        disableRipple
                        disabled={busy !== null || importBlocked}
                        onClick={handleImport}
                        sx={styles.secondaryButton}
                      >
                        {busy === "import" ? (
                          <CircularProgress
                            size={16}
                            sx={{ color: "inherit" }}
                          />
                        ) : (
                          <ImportGlyph sx={{ width: 16, height: 16 }} />
                        )}
                        {isKit
                          ? "Import kit to My templates"
                          : "Import to My templates"}
                      </Button>
                    </Box>
                  </Tooltip>
                )}
                {permissions.canImport && (
                  <Box component="span" sx={styles.actionNote}>
                    {isKit
                      ? documents.length > 0
                        ? `Importing adds the kit to My templates with its ${selectedCount} selected ${
                            selectedCount === 1 ? "document" : "documents"
                          }. Each one stays editable.`
                        : `Importing adds the kit to My templates with all ${documentsLabel(
                            template.documentCount,
                          )}. Each one stays editable.`
                      : "Importing saves an editable copy in My templates so you can adjust clauses and fields before sending."}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={styles.panel}>
            <Box sx={styles.stepsTitle}>How it works</Box>
            <Box component="ol" sx={styles.steps}>
              {(isKit ? KIT_STEPS : SINGLE_STEPS).map((step, i) => (
                <Box component="li" key={step.title} sx={styles.step}>
                  <Box component="span" sx={styles.stepNum}>
                    {i + 1}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1px",
                      pt: "2px",
                    }}
                  >
                    <Box component="span" sx={styles.stepTitle}>
                      {step.title}
                    </Box>
                    <Box component="span" sx={styles.stepText}>
                      {step.text}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateLibraryDetail;
